import { z } from "zod";

export const taggedContextItemTypeSchema = z.enum(["dashboard", "insight"]);

export const taggedContextItemSchema = z
	.object({
		id: z.string().min(1),
		type: taggedContextItemTypeSchema,
		label: z.string().min(1),
	})
	.strict();

export const chatSeedTagsSchema = z
	.object({
		tagged: z.array(taggedContextItemSchema).min(1).max(12).optional(),
	})
	.strict();

export type TaggedContextItem = z.infer<typeof taggedContextItemSchema>;
export type ChatSeedTags = z.infer<typeof chatSeedTagsSchema>;

function normalizeTaggedContextItems(
	value: unknown,
): TaggedContextItem[] | undefined {
	if (!Array.isArray(value)) {
		return undefined;
	}

	const seenIds = new Set<string>();
	const normalizedItems: TaggedContextItem[] = [];
	for (const item of value) {
		if (!item || typeof item !== "object") {
			continue;
		}

		const candidate = item as Partial<TaggedContextItem>;
		if (typeof candidate.id !== "string" || candidate.id.length === 0) {
			continue;
		}
		const parsedType = taggedContextItemTypeSchema.safeParse(candidate.type);
		if (!parsedType.success) {
			continue;
		}
		if (typeof candidate.label !== "string" || candidate.label.length === 0) {
			continue;
		}
		const dedupeKey = `${candidate.type}:${candidate.id}`;
		if (seenIds.has(dedupeKey)) {
			continue;
		}

		seenIds.add(dedupeKey);
		normalizedItems.push({
			id: candidate.id,
			type: parsedType.data,
			label: candidate.label,
		});
	}

	return normalizedItems.length > 0 ? normalizedItems : undefined;
}

export function normalizeChatSeedTags(input: unknown): ChatSeedTags | null {
	if (!input || typeof input !== "object") {
		return null;
	}

	const value = input as {
		tagged?: unknown;
	};

	const normalized = {
		tagged: normalizeTaggedContextItems(value.tagged),
	};

	if (!normalized.tagged) {
		return null;
	}

	const parseResult = chatSeedTagsSchema.safeParse(normalized);
	return parseResult.success ? parseResult.data : null;
}
